// Important: LogRocket has to be the first import and LogRocket.init must happen first
//    so that it can replace the implementations of fetch etc. for logging.
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import { SBStorage } from './helpers';
import { SBBuild } from './version';

const storageKey = 'SBLogger-enabled';
export const browserState = {
  enabled: () => SBStorage.getItem(storageKey) === 'on',
  set: (enable: boolean) =>
    SBStorage.setItem(storageKey, enable ? 'on' : 'off'),
};

// Only enabled for compiled apps - in debug, logging gets in the way of call stacks and breakpoints etc., hence not needed
const enabled =
  process.env.NODE_ENV === 'production' &&
  (SB_CONFIG.logging === 'on' || browserState.enabled());

// removes any personally identifiable information (PII) - case insensitive search
const piiFields = ['address', 'phone', 'email', 'mobile', 'customer'];
const isPiiField = (fieldName: string) =>
  // err on the side of caution - if the fieldname even contains a piiFieldName, remove it
  piiFields.some(piiField =>
    fieldName.toLowerCase().includes(piiField.toLowerCase()),
  );

const doSanitise = (obj: any): any => {
  if (!obj) return obj;

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (isPiiField(key)) {
        return [key, '<redacted>'];
      }

      if (Array.isArray(value)) {
        // recurse the array values
        return [key, value.map(elem => doSanitise(elem))];
      }

      // recurse down any objects
      if (typeof value === 'object') {
        return [key, doSanitise(value)];
      }

      // just a primitive, safe to return
      return [key, value];
    }),
  );
};
const sanitise = (obj: any): any => {
  return doSanitise(obj);
};

// Note: wrap any calls to LogRocket here, so that we can easily replace the Logging provider in the future if necessary
export const SBLogger = {
  init: () => {
    if (enabled) {
      LogRocket.init('0dgqys/reactapps', {
        release: `V${SBBuild.major}v${SBBuild.minor}`,
        network: {
          requestSanitizer: sanitise,
          responseSanitizer: sanitise,
        },
      });

      setupLogRocketReact(LogRocket);
    }
  },

  identify: (user: any /* CRUserBody, but can't use the type here */) => {
    if (enabled && user) {
      const baseURL = new URL(window.location.pathname, window.location.origin);
      const userId = baseURL.href + ' usr=' + user.userId;

      LogRocket.identify(userId, {
        name: user.userName,
        email: SBBuild.app, // just use the email field for app name - we can easily filter by email in the LogRocket dashboard

        site: user.siteId,
        branch: user.siteBranchName,
        ...SBBuild,
      });
    }
  },

  middleware: () =>
    enabled
      ? LogRocket.reduxMiddleware({
          stateSanitizer: sanitise,
          actionSanitizer: sanitise,
        })
      : undefined,

  isPiiField,
};
